import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ShieldAlert } from "lucide-react";

/**
 * Route wrapper che blocca l'accesso diretto (via URL) a una sezione
 * quando l'utente non ha il permesso corrispondente.
 *
 * Regole:
 * - salon_owner e agency_admin bypassano sempre (accesso totale)
 * - staff senza il permesso richiesto → schermata "accesso negato"
 * - se l'utente è ancora in caricamento, restituisce null (evita flicker)
 *
 * NOTA: la nav sidebar già nasconde le voci non permesse; questo wrapper
 * copre il caso in cui uno staff prova a forzare l'URL manualmente.
 */
export function PermGuard({ perm, children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace state={{ from: location }} />;
  const role = user.role;
  if (role === "salon_owner" || role === "agency_admin") return children;
  const permissions = user.permissions || [];
  if (!perm || permissions.includes(perm)) return children;
  return <PermDenied perm={perm} />;
}

function PermDenied({ perm }) {
  return (
    <div className="p-8" data-testid="perm-denied">
      <div className="sp-card p-10 max-w-lg mx-auto text-center space-y-4">
        <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center mx-auto">
          <ShieldAlert className="h-7 w-7 text-red-500" />
        </div>
        <h1 className="font-display text-2xl font-black">Accesso non consentito</h1>
        <p className="text-sm text-[color:var(--sp-text-secondary)]">
          Il tuo profilo non ha il permesso per la sezione <b>{perm}</b>.
          Chiedi al titolare del salone di abilitarti se necessario.
        </p>
        <a href="/app" className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[color:var(--sp-brand)] text-white text-sm font-bold hover:opacity-90">
          Torna alla dashboard
        </a>
      </div>
    </div>
  );
}
